import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  ChevronLeft, Copy, Check, ExternalLink, Camera, Save, Pencil,
  Video, Briefcase, Plus, Trash2, X, Mail, Phone, Building2, History,
  Sparkles, MapPin, Instagram, Youtube, Linkedin, Globe, Twitter,
  Film, Image as ImageIcon, Send,
} from "lucide-react";

/* User public profile — cinematic redesign · Feb 2026.
 *
 *  Two routes:
 *    · /me/public-profile        → owner edit (auth)
 *    · /u/:handle                → public view (anonymous)
 *
 *  Layout (top to bottom):
 *    · Cover band                — user-uploaded cover, or subtle gradient
 *    · Overlap hero card         — avatar · name · availability · CTA
 *    · Bio + social links row
 *    · Shoot Moments strip       — moderate posting, 1 photo · 3/24h
 *    · Showreel gallery
 *    · Work timeline + manual highlights (unchanged from prior iter)
 *    · Owner editor (behind a toggle)
 */
export default function UserPublicProfile({ mode = "public" }) {
  const params  = useParams();
  const handle  = params.handle;
  const isOwner = mode === "owner";

  const [data, setData]       = useState(null);
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isOwner) {
        const { data: p }   = await api.get("/users/mine/public-profile");
        const { data: pub } = await api.get(`/public/u/${p.public_handle}`)
                                       .catch(() => ({ data: { work_history: [] } }));
        setData({ profile: p, work_history: pub.work_history || [] });
        const { data: m } = await api.get("/moments/mine").catch(() => ({ data: { moments: [] } }));
        setMoments(m.moments || []);
      } else {
        const { data: pub } = await api.get(`/public/u/${handle}`);
        setData(pub);
        const { data: m } = await api.get(`/public/u/${handle}/moments`).catch(() => ({ data: { moments: [] } }));
        setMoments(m.moments || []);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load profile");
    } finally { setLoading(false); }
  }, [isOwner, handle]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-black text-white/60 flex items-center justify-center"
           data-testid="up-loading">
        Loading profile…
      </div>
    );
  }

  const p = data.profile;

  return (
    <div className="min-h-screen bg-black text-white"
         data-testid={isOwner ? "user-public-profile-owner"
                              : "user-public-profile-view"}>
      {isOwner && (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-6">
          <Link to="/dashboard"
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-white/50 hover:text-white transition">
            <ChevronLeft size={12} /> Back to dashboard
          </Link>
        </div>
      )}

      <CoverBand profile={p} isOwner={isOwner} onChanged={load} />
      <HeroCard  profile={p} isOwner={isOwner} onChanged={load} />
      <BioBlock  profile={p} />
      <MomentsStrip moments={moments} isOwner={isOwner}
                    profile={p} onChanged={() => load()} />
      <ShowreelRow  profile={p} />

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-16">
        {data.work_history?.length > 0 && (
          <WorkTimeline history={data.work_history} />
        )}
        <ManualEntries entries={p.manual_work_entries || []}
                       isOwner={isOwner} onChanged={load} />
        {isOwner && (
          <div className="mt-12">
            <OwnerEditor profile={p} onSaved={load} />
          </div>
        )}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  Cover band — Facebook-style full-width cover strip.
 *  Owner sees hover overlay with camera icon → uploads to
 *  POST /users/mine/cover-photo (multipart).
 * ═══════════════════════════════════════════════════════════════════ */
function CoverBand({ profile, isOwner, onChanged }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/users/mine/cover-photo", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cover photo updated");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const src = profile.cover_photo_url || profile.hero_image_url || "";

  return (
    <div className="relative"
         data-testid="up-cover-band">
      <div className="relative h-52 sm:h-64 lg:h-80 overflow-hidden">
        {src ? (
          <img src={src} alt=""
               className="w-full h-full object-cover"
               onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          // Cinema-lit gradient fallback + subtle grain texture
          <div className="absolute inset-0"
               style={{
                 background: "radial-gradient(120% 80% at 20% 0%, #2a1b12 0%, #0a0a0a 55%), radial-gradient(90% 60% at 90% 100%, #1a1129 0%, #050505 60%)",
               }}>
            <div className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
                 style={{
                   backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"120\" height=\"120\"><filter id=\"n\"><feTurbulence type=\"fractalNoise\" baseFrequency=\"0.9\"/></filter><rect width=\"100%25\" height=\"100%25\" filter=\"url(%23n)\"/></svg>')",
                 }} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black" />

        {isOwner && (
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <input ref={fileRef} type="file"
                   accept="image/png,image/jpeg,image/webp"
                   className="hidden"
                   onChange={(e) => { const f = e.target.files?.[0]; upload(f); e.target.value = ""; }}
                   data-testid="up-cover-input" />
            <button type="button" disabled={busy}
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-white/85 hover:text-white bg-black/50 hover:bg-black/70 border border-white/15 hover:border-white/40 backdrop-blur-md rounded-full px-3 py-1.5 transition-all disabled:opacity-40"
                    data-testid="up-cover-upload">
              <Camera size={11} />
              {busy ? "Uploading…" : profile.cover_photo_url ? "Replace" : "Add cover"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  Hero card — avatar, name, handle, availability, contextual CTA.
 *  Overlaps the cover band by -80px on desktop, -56px on mobile.
 * ═══════════════════════════════════════════════════════════════════ */
const AVAILABILITY = {
  hired:               { label: "At work",             color: "#60A5FA", cta: "See reel" },
  available_for_hire:  { label: "Available for hire",  color: "#6EE7B7", cta: "Hire me" },
  freelancer:          { label: "Freelance",           color: "#F59E0B", cta: "Enquire" },
  on_break:            { label: "On a break",          color: "#9CA3AF", cta: "Message" },
};

function HeroCard({ profile, isOwner, onChanged }) {
  const [copied, setCopied]  = useState(false);
  const [busy, setBusy]      = useState(false);
  const avatarRef            = useRef(null);
  const url = `${window.location.origin}/u/${profile.public_handle}`;

  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  };

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/users/mine/avatar", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Avatar updated");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const avail = AVAILABILITY[profile.availability_status] || AVAILABILITY.hired;
  const primaryCta = _primaryCta(profile, avail);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 -mt-14 sm:-mt-20 relative z-10">
      <div className="rounded-3xl border border-white/10 bg-black/70 backdrop-blur-xl px-5 sm:px-8 py-6 sm:py-7 shadow-[0_18px_60px_-15px_rgba(0,0,0,0.6)]"
           data-testid="up-hero-card">
        <div className="flex flex-col sm:flex-row sm:items-end gap-5">
          {/* Avatar */}
          <div className="relative shrink-0 -mt-16 sm:-mt-24">
            <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl overflow-hidden bg-white/5 ring-2 ring-black/40 shadow-[0_10px_30px_-8px_rgba(0,0,0,0.7)]"
                 data-testid="up-avatar-wrap">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt=""
                     className="w-full h-full object-cover"
                     onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/50">
                  <ImageIcon size={26} />
                </div>
              )}
            </div>
            {isOwner && (
              <>
                <input ref={avatarRef} type="file"
                       accept="image/png,image/jpeg,image/webp"
                       className="hidden"
                       onChange={(e) => { const f = e.target.files?.[0]; upload(f); e.target.value = ""; }}
                       data-testid="up-avatar-input" />
                <button type="button" disabled={busy}
                        onClick={() => avatarRef.current?.click()}
                        className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-white text-black hover:bg-white/90 flex items-center justify-center shadow-lg disabled:opacity-50"
                        aria-label="Change avatar"
                        data-testid="up-avatar-upload">
                  <Camera size={14} />
                </button>
              </>
            )}
          </div>

          {/* Name + meta */}
          <div className="min-w-0 flex-1">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">
              @{profile.public_handle}
            </div>
            <h1 className="font-display text-3xl sm:text-5xl uppercase tracking-tight leading-[0.95] mt-1"
                data-testid="up-name">
              {profile.name}
            </h1>
            {profile.headline && (
              <div className="mt-2 text-white/80 text-base sm:text-lg"
                   data-testid="up-headline">{profile.headline}</div>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <AvailabilityPill avail={avail} />
              {profile.location_city && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10.5px] tracking-[0.18em] uppercase bg-white/[0.06] text-white/70"
                      data-testid="up-location">
                  <MapPin size={11} /> {profile.location_city}
                </span>
              )}
              {profile.current_org?.handle && (
                <Link to={`/agency/${profile.current_org.handle}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10.5px] tracking-[0.18em] uppercase bg-white/[0.06] hover:bg-white/[0.12] text-white/80"
                      data-testid="up-current-org-link">
                  <Building2 size={11} /> {profile.current_org.name}
                  <ExternalLink size={10} />
                </Link>
              )}
            </div>
          </div>

          {/* Action rail */}
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {primaryCta && (
              <a href={primaryCta.href}
                 target={primaryCta.external ? "_blank" : undefined}
                 rel={primaryCta.external ? "noopener noreferrer" : undefined}
                 className="h-10 px-5 rounded-full inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-semibold bg-white text-black hover:bg-[color:var(--association-theme,#F5B82E)] transition-colors"
                 data-testid="up-primary-cta">
                {primaryCta.icon}
                {primaryCta.label}
              </a>
            )}
            <button type="button" onClick={copy}
                    className="h-10 w-10 rounded-full inline-flex items-center justify-center border border-white/15 hover:border-white/40 text-white/70 hover:text-white transition-colors"
                    aria-label="Copy profile URL"
                    data-testid="up-copy-btn">
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AvailabilityPill({ avail }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10.5px] tracking-[0.18em] uppercase font-semibold"
          style={{ background: `${avail.color}22`, color: avail.color }}
          data-testid="up-availability">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: avail.color }} />
      {avail.label}
    </span>
  );
}

function _primaryCta(p, avail) {
  // Contextual CTA — hierarchy: reel link > email > phone > fallback.
  if (p.showreel_urls?.length && p.availability_status === "hired") {
    return { label: "See Reel", icon: <Video size={12} />,
             href: p.showreel_urls[0], external: true };
  }
  if (p.contact_email) {
    return { label: avail.cta, icon: <Mail size={12} />,
             href: `mailto:${p.contact_email}?subject=via%20Call%20Sheet`, external: false };
  }
  if (p.contact_phone) {
    return { label: avail.cta, icon: <Phone size={12} />,
             href: `tel:${p.contact_phone}`, external: false };
  }
  return null;
}


/* ═══════════════════════════════════════════════════════════════════
 *  Bio + social links row
 * ═══════════════════════════════════════════════════════════════════ */
const SOCIAL_ICONS = {
  instagram: Instagram, x: Twitter, youtube: Youtube,
  linkedin:  Linkedin, imdb: Film, vimeo: Film,
  behance:   Sparkles, website: Globe,
};

function BioBlock({ profile }) {
  const sl = profile.social_links || {};
  const socialEntries = Object.entries(sl).filter(([, v]) => (v || "").trim());
  if (!profile.bio && !socialEntries.length && !profile.tagline_now) return null;

  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 mt-6"
             data-testid="up-bio-block">
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          {profile.tagline_now && (
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-400/25 text-emerald-200 text-[10.5px] tracking-[0.22em] uppercase px-3 py-1.5 mb-3"
                 data-testid="up-tagline-now">
              <Sparkles size={11} /> {profile.tagline_now}
            </div>
          )}
          {profile.bio && (
            <p className="text-[15px] sm:text-base text-white/85 leading-relaxed max-w-3xl"
               data-testid="up-bio">{profile.bio}</p>
          )}
        </div>
        {socialEntries.length > 0 && (
          <div className="flex md:justify-end flex-wrap gap-2 self-start"
               data-testid="up-social-links">
            {socialEntries.map(([k, v]) => {
              const Icon = SOCIAL_ICONS[k] || Globe;
              return (
                <a key={k} href={_normaliseUrl(v)}
                   target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.05] hover:bg-white/[0.12] border border-white/10 hover:border-white/25 text-white/75 hover:text-white text-[10.5px] tracking-[0.2em] uppercase px-3 py-1.5 transition-colors"
                   data-testid={`up-social-${k}`}>
                  <Icon size={11} /> {k}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function _normaliseUrl(u) {
  if (!u) return "#";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("@")) return `https://instagram.com/${u.slice(1)}`;
  if (u.startsWith("www.")) return `https://${u}`;
  return `https://${u}`;
}


/* ═══════════════════════════════════════════════════════════════════
 *  Shoot Moments strip — moderate posting, 3/24h. Owner sees a
 *  compose button that inlines a small file-picker + caption.
 * ═══════════════════════════════════════════════════════════════════ */
function MomentsStrip({ moments, isOwner, profile, onChanged }) {
  const [composing, setComposing] = useState(false);
  if (!moments.length && !isOwner) return null;

  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 mt-12"
             data-testid="up-moments">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
            Live from the set
          </div>
          <h2 className="font-display text-2xl uppercase tracking-tight mt-1">
            Shoot Moments
          </h2>
        </div>
        {isOwner && (
          <button type="button" onClick={() => setComposing((s) => !s)}
                  className="h-9 px-4 rounded-full text-[10.5px] tracking-[0.22em] uppercase font-semibold border border-white/25 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-1.5"
                  data-testid="up-moments-compose-toggle">
            {composing ? <><X size={12} /> Close</>
                       : <><Plus size={12} /> New Moment</>}
          </button>
        )}
      </div>

      {isOwner && composing && (
        <MomentComposer onDone={() => { setComposing(false); onChanged(); }} />
      )}

      {moments.length === 0 ? (
        <div className="mt-5 rounded-2xl border border-dashed border-white/10 bg-white/[0.02] py-8 text-center"
             data-testid="up-moments-empty">
          <ImageIcon size={20} className="text-white/25 mx-auto mb-2" />
          <div className="text-[10.5px] tracking-[0.22em] uppercase text-white/55">
            {isOwner ? "Share your first moment from the set"
                     : "No moments yet"}
          </div>
        </div>
      ) : (
        <div className="mt-5 -mx-4 sm:-mx-0 overflow-x-auto no-scrollbar"
             data-testid="up-moments-scroll">
          <div className="flex gap-4 px-4 sm:px-0 pb-2 snap-x snap-mandatory">
            {moments.map((m) => (
              <MomentCard key={m.id} m={m} isOwner={isOwner}
                          profile={profile} onDelete={onChanged} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function MomentCard({ m, isOwner, profile, onDelete }) {
  const photo = m.photo_url && m.photo_url.startsWith("emergent:")
    ? `/api/public/u/${profile.public_handle}/moments/${m.id}/photo`
    : m.photo_url;
  return (
    <div className="snap-start shrink-0 w-56 sm:w-64 rounded-2xl overflow-hidden bg-white/[0.03] border border-white/10 relative group"
         data-testid={`up-moment-${m.id}`}>
      <div className="aspect-[4/5] bg-black/40 overflow-hidden">
        {photo ? (
          <img src={photo} alt=""
               className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
               onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/25">
            <ImageIcon size={22} />
          </div>
        )}
      </div>
      <div className="p-3">
        {m.caption && (
          <div className="text-[12px] text-white/85 leading-snug line-clamp-2">
            {m.caption}
          </div>
        )}
        <div className="mt-1.5 text-[10px] tracking-[0.18em] uppercase text-white/45 flex items-center gap-2 flex-wrap">
          {m.location && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {m.location}</span>}
          {m.project_title && <span className="text-white/60">· {m.project_title}</span>}
          <span className="ml-auto tabular-nums">{_relDate(m.created_at)}</span>
        </div>
      </div>
      {isOwner && (
        <button type="button"
                onClick={async () => {
                  if (!window.confirm("Delete this moment?")) return;
                  try {
                    await api.delete(`/moments/${m.id}`);
                    toast.success("Deleted");
                    onDelete();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Delete failed");
                  }
                }}
                className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 text-white/70 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                data-testid={`up-moment-delete-${m.id}`}>
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

function MomentComposer({ onDone }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const preview = file ? URL.createObjectURL(file) : null;

  const submit = async () => {
    if (!file) { toast.error("Add a photo first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      fd.append("location", location);
      const { data } = await api.post("/moments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Posted · ${data.remaining_today} more today`);
      onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Post failed";
      toast.error(detail);
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5"
         data-testid="up-moment-composer">
      <div className="flex flex-col sm:flex-row gap-4">
        <label className="cursor-pointer relative shrink-0 w-full sm:w-36 h-40 sm:h-44 rounded-xl border border-dashed border-white/15 bg-black/30 flex items-center justify-center overflow-hidden hover:border-white/35 transition-colors"
               data-testid="up-moment-composer-photo-label">
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="text-center px-3">
              <Camera size={20} className="text-white/40 mx-auto mb-1" />
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">
                Add photo
              </div>
            </div>
          )}
          <input type="file"
                 accept="image/png,image/jpeg,image/webp"
                 className="hidden"
                 onChange={(e) => setFile(e.target.files?.[0] || null)}
                 data-testid="up-moment-composer-photo" />
        </label>
        <div className="flex-1 min-w-0">
          <label className="text-[10px] tracking-[0.22em] uppercase text-white/50">Caption · ≤ 240 chars</label>
          <textarea value={caption} maxLength={240}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="e.g. Day 118 on Saas Bahu — wrap in sight."
                    className="mt-1 w-full h-20 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm resize-none focus:border-white/30 focus:outline-none"
                    data-testid="up-moment-composer-caption" />
          <div className="mt-2 flex gap-2 flex-wrap">
            <input type="text" value={location} maxLength={120}
                   onChange={(e) => setLocation(e.target.value)}
                   placeholder="Location · e.g. Mudh Island"
                   className="flex-1 min-w-[180px] h-9 bg-black/40 border border-white/10 rounded-lg px-3 text-[13px] focus:border-white/30 focus:outline-none"
                   data-testid="up-moment-composer-location" />
            <button type="button" onClick={submit} disabled={busy || !file}
                    className="h-9 px-5 rounded-full text-[10.5px] tracking-[0.22em] uppercase font-semibold bg-white text-black hover:bg-white/90 disabled:opacity-40 inline-flex items-center gap-1.5"
                    data-testid="up-moment-composer-submit">
              <Send size={11} /> {busy ? "Posting…" : "Post"}
            </button>
          </div>
          <div className="mt-2 text-[10px] tracking-[0.16em] uppercase text-white/40">
            Max 3 moments per 24h — keeps the feed calm.
          </div>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  Showreel row
 * ═══════════════════════════════════════════════════════════════════ */
function ShowreelRow({ profile }) {
  const reels = profile.showreel_urls || [];
  if (!reels.length) return null;
  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 mt-12"
             data-testid="up-showreel">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
        Selected work · links
      </div>
      <h2 className="font-display text-2xl uppercase tracking-tight mt-1">
        Showreel
      </h2>
      <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
        {reels.map((u, i) => (
          <a key={u} href={u} target="_blank" rel="noreferrer"
             className="group relative aspect-[16/9] rounded-xl overflow-hidden bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/30 flex items-center justify-center transition-colors"
             data-testid={`up-showreel-${i}`}>
            <Video size={26} className="text-white/40 group-hover:text-white transition-colors" />
            <div className="absolute bottom-2 left-2 right-2 text-[10.5px] tracking-[0.22em] uppercase text-white/70">
              Reel {i + 1}
            </div>
            <ExternalLink size={12}
                          className="absolute top-2 right-2 text-white/40 group-hover:text-white" />
          </a>
        ))}
      </div>
    </section>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  Owner editor — new fields (cover, avatar, location, socials)
 *  live in dedicated UI (avatar + cover cameras above); this modal
 *  now only edits the text fields.
 * ═══════════════════════════════════════════════════════════════════ */
function OwnerEditor({ profile, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    bio:              profile.bio || "",
    headline:         profile.headline || "",
    tagline_now:      profile.tagline_now || "",
    location_city:    profile.location_city || "",
    hero_video_url:   profile.hero_video_url || "",
    showreel_urls:    (profile.showreel_urls || []).join("\n"),
    availability_status: profile.availability_status || "hired",
    contact_email_public: !!profile.contact_email,
    contact_phone_public: !!profile.contact_phone,
    public_handle:    profile.public_handle || "",
    social_links:     {
      instagram: profile.social_links?.instagram || "",
      x:         profile.social_links?.x         || "",
      youtube:   profile.social_links?.youtube   || "",
      linkedin:  profile.social_links?.linkedin  || "",
      imdb:      profile.social_links?.imdb      || "",
      website:   profile.social_links?.website   || "",
    },
  });
  const [busy, setBusy] = useState(false);
  const handleLocked = !!profile.public_handle_locked;

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        bio: form.bio, headline: form.headline,
        tagline_now: form.tagline_now,
        location_city: form.location_city,
        hero_video_url: form.hero_video_url,
        showreel_urls: form.showreel_urls.split(/\n/).map((s) => s.trim()).filter(Boolean),
        availability_status: form.availability_status,
        contact_email_public: form.contact_email_public,
        contact_phone_public: form.contact_phone_public,
        social_links: form.social_links,
      };
      if (!handleLocked && form.public_handle && form.public_handle !== profile.public_handle) {
        body.public_handle = form.public_handle;
      }
      await api.patch("/users/mine/public-profile", body);
      toast.success("Profile saved");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"
         data-testid="up-owner-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
            Owner
          </div>
          <h3 className="font-display text-2xl uppercase tracking-tight mt-1">
            Edit profile
          </h3>
        </div>
        <button onClick={() => setOpen((s) => !s)}
                className="h-9 px-4 rounded-full text-[10.5px] tracking-[0.2em] uppercase font-semibold border border-white/25 hover:bg-white hover:text-black transition inline-flex items-center gap-1.5"
                data-testid="up-toggle-editor">
          <Pencil size={12} /> {open ? "Close" : "Open editor"}
        </button>
      </div>

      {open && (
        <div className="mt-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Headline · one line</label>
              <input value={form.headline} maxLength={120}
                     onChange={(e) => setForm((s) => ({ ...s, headline: e.target.value }))}
                     placeholder="e.g. Executive Creative Director · Mumbai"
                     className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
                     data-testid="up-headline-input" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Location · city</label>
              <input value={form.location_city} maxLength={80}
                     onChange={(e) => setForm((s) => ({ ...s, location_city: e.target.value }))}
                     placeholder="Mumbai"
                     className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
                     data-testid="up-location-input" />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Right-now tagline · e.g. &ldquo;On set at Yash Raj Films&rdquo;</label>
            <input value={form.tagline_now} maxLength={120}
                   onChange={(e) => setForm((s) => ({ ...s, tagline_now: e.target.value }))}
                   className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
                   data-testid="up-tagline-input" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Bio · ≤ 800 chars</label>
            <textarea value={form.bio} maxLength={800}
                      onChange={(e) => setForm((s) => ({ ...s, bio: e.target.value }))}
                      className="mt-1 w-full h-24 bg-black/40 border border-white/15 rounded-md px-3 py-2 text-sm resize-none"
                      placeholder="Craft your intro — how you work, awards, brands you've served, what excites you."
                      data-testid="up-bio-input" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Showreel URLs · one per line, up to 6
            </label>
            <textarea value={form.showreel_urls}
                      onChange={(e) => setForm((s) => ({ ...s, showreel_urls: e.target.value }))}
                      className="mt-1 w-full h-20 bg-black/40 border border-white/15 rounded-md px-3 py-2 text-sm resize-none font-mono"
                      placeholder={"https://youtu.be/…\nhttps://vim.co/…"}
                      data-testid="up-showreel-input" />
          </div>

          {/* Social links grid */}
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Social links</label>
            <div className="mt-1 grid sm:grid-cols-2 gap-2">
              {["instagram", "x", "youtube", "linkedin", "imdb", "website"].map((k) => (
                <input key={k}
                       value={form.social_links[k]}
                       onChange={(e) => setForm((s) => ({ ...s,
                         social_links: { ...s.social_links, [k]: e.target.value },
                       }))}
                       placeholder={k}
                       className="bg-black/40 border border-white/15 rounded-md h-9 px-3 text-[12px]"
                       data-testid={`up-social-input-${k}`} />
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Availability</label>
              <select value={form.availability_status}
                      onChange={(e) => setForm((s) => ({ ...s, availability_status: e.target.value }))}
                      className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
                      data-testid="up-availability-input">
                <option value="hired">At work · hired</option>
                <option value="available_for_hire">Available for hire</option>
                <option value="freelancer">Freelance</option>
                <option value="on_break">On a break</option>
              </select>
            </div>
            <label className="flex items-end pb-1 gap-2 text-[12px] text-white/70">
              <input type="checkbox" checked={form.contact_email_public}
                     onChange={(e) => setForm((s) => ({ ...s, contact_email_public: e.target.checked }))}
                     data-testid="up-email-public" />
              Show email publicly
            </label>
            <label className="flex items-end pb-1 gap-2 text-[12px] text-white/70">
              <input type="checkbox" checked={form.contact_phone_public}
                     onChange={(e) => setForm((s) => ({ ...s, contact_phone_public: e.target.checked }))}
                     data-testid="up-phone-public" />
              Show phone publicly
            </label>
          </div>

          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Handle · @{form.public_handle || profile.public_handle}
              {handleLocked && <span className="ml-2 text-amber-300/80">(locked — one-time change already used)</span>}
            </label>
            <input value={form.public_handle} disabled={handleLocked}
                   onChange={(e) => setForm((s) => ({ ...s, public_handle: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") }))}
                   className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm disabled:opacity-50"
                   data-testid="up-handle-input" />
          </div>

          <div className="flex justify-end">
            <button onClick={save} disabled={busy}
                    className="h-10 px-6 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold bg-white text-black hover:bg-white/90 disabled:opacity-60 inline-flex items-center gap-2"
                    data-testid="up-save">
              <Save size={12} /> {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  Work timeline + Manual entries — unchanged from prior iter,
 *  restyled slightly to match new dark card language.
 * ═══════════════════════════════════════════════════════════════════ */
function WorkTimeline({ history }) {
  return (
    <section className="mt-12" data-testid="up-work-timeline">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Auto-linked</div>
      <h2 className="font-display text-2xl uppercase tracking-tight mt-1">Work History</h2>
      <ul className="mt-5 space-y-3">
        {history.map((e) => (
          <li key={e.id}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3"
              data-testid={`up-history-${e.id}`}>
            <div className="w-9 h-9 rounded-full bg-white/[0.06] flex items-center justify-center text-white/70">
              <History size={14} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] text-white/85">
                {e.kind === "role_change" && <>Became <b>{e.value_label || e.value}</b></>}
                {e.kind === "desk_change" && <>Moved to <b>{(e.value || "").replace(/_/g, " ")}</b> desk</>}
                {e.kind === "branch_change" && <>Shifted branch</>}
                {e.kind === "status_change" && <>Status changed</>}
                {e.org_handle ? (
                  <> at <Link to={`/agency/${e.org_handle}`} className="text-white/70 hover:text-white underline underline-offset-2">{e.org_name}</Link></>
                ) : e.org_name ? (
                  <> at <span className="text-white/70">{e.org_name}</span></>
                ) : null}
              </div>
              <div className="mt-0.5 text-[10.5px] text-white/45 tabular-nums">
                {new Date(e.at).toLocaleDateString()}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ManualEntries({ entries, isOwner, onChanged }) {
  const [adding, setAdding] = useState(false);
  return (
    <section className="mt-12" data-testid="up-manual-work">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Highlights added by me</div>
          <h2 className="font-display text-2xl uppercase tracking-tight mt-1">Selected Work</h2>
        </div>
        {isOwner && (
          <button onClick={() => setAdding(true)}
                  className="h-9 px-4 rounded-full text-[10.5px] tracking-[0.2em] uppercase font-semibold border border-white/25 hover:bg-white hover:text-black transition inline-flex items-center gap-1.5"
                  data-testid="up-add-work-btn">
            <Plus size={12} /> Add Highlight
          </button>
        )}
      </div>

      {entries.length === 0 ? (
        <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] p-8 text-center text-sm text-white/50">
          {isOwner ? "Add a Cannes trophy, a landmark campaign, or a favourite piece."
                   : "No highlights added yet."}
        </div>
      ) : (
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {entries.map((e) => (
            <div key={e.id}
                 className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden group relative"
                 data-testid={`up-manual-${e.id}`}>
              {e.thumb_url ? (
                <a href={e.media_url || "#"} target="_blank" rel="noreferrer">
                  <img src={e.thumb_url} alt="" className="w-full h-40 object-cover"
                       onError={(ev) => { ev.target.style.display = "none"; }} />
                </a>
              ) : (
                <div className="h-40 bg-white/[0.05] flex items-center justify-center text-white/30">
                  <Briefcase size={22} />
                </div>
              )}
              <div className="p-4">
                <div className="font-display text-sm uppercase tracking-tight">{e.title}</div>
                <div className="mt-0.5 text-[11px] text-white/55">
                  {e.role} {e.org_name && `· ${e.org_name}`}
                </div>
                {(e.year_from || e.year_to) && (
                  <div className="text-[10.5px] text-white/40 tabular-nums">
                    {e.year_from}{e.year_to && e.year_to !== e.year_from ? ` — ${e.year_to}` : ""}
                  </div>
                )}
                {e.summary && <p className="mt-2 text-[11.5px] text-white/60 line-clamp-3">{e.summary}</p>}
              </div>
              {isOwner && (
                <button onClick={async () => {
                  if (!window.confirm("Delete this highlight?")) return;
                  await api.delete(`/users/mine/work-entries/${e.id}`);
                  toast.success("Deleted"); onChanged();
                }}
                        className="absolute top-2 right-2 p-1.5 rounded-md bg-black/60 text-white/60 hover:text-red-300 opacity-0 group-hover:opacity-100 transition"
                        data-testid={`up-manual-delete-${e.id}`}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {adding && <AddWorkModal onClose={() => setAdding(false)}
                                onSaved={() => { setAdding(false); onChanged(); }} />}
    </section>
  );
}

function AddWorkModal({ onClose, onSaved }) {
  const [f, setF] = useState({
    title: "", role: "", org_name: "", year_from: "", year_to: "",
    summary: "", media_url: "", thumb_url: "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const body = {
        ...f,
        year_from: f.year_from ? parseInt(f.year_from, 10) : null,
        year_to:   f.year_to ? parseInt(f.year_to, 10) : null,
      };
      await api.post("/users/mine/work-entries", body);
      toast.success("Highlight added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
         data-testid="up-add-work-modal">
      <div className="bg-neutral-950 border border-white/10 rounded-2xl max-w-lg w-full p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg uppercase tracking-tight">Add Highlight</h3>
          <button onClick={onClose}
                  className="text-white/50 hover:text-white"
                  data-testid="up-add-work-close">
            <X size={18} />
          </button>
        </div>
        <input value={f.title} onChange={(e) => upd("title", e.target.value)}
               placeholder="Project title *"
               className="w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <input value={f.role} onChange={(e) => upd("role", e.target.value)}
                 placeholder="Role"
                 className="bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm" />
          <input value={f.org_name} onChange={(e) => upd("org_name", e.target.value)}
                 placeholder="Org / brand"
                 className="bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input value={f.year_from} onChange={(e) => upd("year_from", e.target.value)}
                 placeholder="Year from" type="number"
                 className="bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm" />
          <input value={f.year_to} onChange={(e) => upd("year_to", e.target.value)}
                 placeholder="Year to (optional)" type="number"
                 className="bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm" />
        </div>
        <textarea value={f.summary} maxLength={400}
                  onChange={(e) => upd("summary", e.target.value)}
                  placeholder="Short summary (≤ 400 chars)"
                  className="w-full h-20 bg-black/40 border border-white/15 rounded-md px-3 py-2 text-sm resize-none" />
        <input value={f.thumb_url} onChange={(e) => upd("thumb_url", e.target.value)}
               placeholder="Thumbnail URL (optional)"
               className="w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm" />
        <input value={f.media_url} onChange={(e) => upd("media_url", e.target.value)}
               placeholder="Media URL (link to reel / article)"
               className="w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm" />
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold border border-white/20 hover:bg-white/10">
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
                  className="h-10 px-6 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold bg-white text-black hover:bg-white/90 disabled:opacity-60 inline-flex items-center gap-2"
                  data-testid="up-add-work-submit">
            <Save size={12} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* Tiny relative-time helper */
function _relDate(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60)   return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}
